import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "../ui/Button";
import {
  SYNCED_MARKER_MS,
  isFailedMarker,
  markerFor,
  markerLabel,
  reasonFor,
  sheetsRowUrl,
  type SyncChange,
  type SyncMarkerKind,
} from "../../lib/sync-state";

const MARKER_TONE: Record<SyncMarkerKind, string> = {
  pending: "border-od-border bg-od-surface-2 text-od-muted",
  syncing:
    "border-[color-mix(in_srgb,var(--od-primary)_35%,transparent)] bg-[color-mix(in_srgb,var(--od-primary)_10%,transparent)] text-od-primary-text",
  synced:
    "border-[color-mix(in_srgb,var(--od-success)_35%,transparent)] bg-[color-mix(in_srgb,var(--od-success)_10%,transparent)] text-od-success",
  retrying:
    "border-[color-mix(in_srgb,var(--od-warning)_40%,transparent)] bg-[color-mix(in_srgb,var(--od-warning)_12%,transparent)] text-od-warning",
  needs_action:
    "border-[color-mix(in_srgb,var(--od-danger)_40%,transparent)] bg-[color-mix(in_srgb,var(--od-danger)_10%,transparent)] text-od-danger",
};

/** Rows mount per page, not per edit, so one shared 1 s tick is enough. */
export const useSecondTick = (active: boolean): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
};

/** Keeps the focus order inside the popover while it is open. */
const FOCUSABLE =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const focusables = (root: HTMLElement | null): HTMLElement[] => {
  if (!root) return [];
  // No visibility filter: jsdom has no layout, so every node looks hidden.
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
};

type SyncStatusMarkerProps = {
  change: SyncChange | null;
  /** Google file id, for the "Open in Google Sheets" action. */
  spreadsheetId?: string | null;
  /** When this marker first saw the `SYNCED` item, for the 3 s rule. */
  syncedAt?: number | null;
  /** Lets the grid ignore its key handlers while the popover is open. */
  onPopoverOpenChange?: (open: boolean) => void;
  onRetry?: (changeId: string) => void;
  onDismiss?: (changeId: string) => void;
  /**
   * A view-only member still sees why a row is out of sync, but not Retry or
   * Dismiss: both are writes and the API answers 403 (MOSA-367 slice 1).
   */
  viewOnly?: boolean;
};

/**
 * The per-row sync marker (ARD 9.6). It is not a live region: a bulk edit
 * would otherwise read every row out loud. The header carries the live count.
 *
 * The failure popover is `role="dialog"`. It closes on Escape and on an
 * outside click, takes focus when it opens and gives it back to the marker when
 * it closes, and keeps Tab inside itself while it is open.
 *
 * It renders through a React portal on `document.body`. An `absolute` child of
 * the cell was clipped by the DataGrid cell, which scrolls and hides overflow.
 */
export const SyncStatusMarker: React.FC<SyncStatusMarkerProps> = ({
  change,
  spreadsheetId,
  syncedAt,
  onPopoverOpenChange,
  onRetry,
  onDismiss,
  viewOnly = false,
}) => {
  const marker = markerFor(change, Date.now());
  // "Synced" hides after 3 s, so the render ticks even when nothing counts
  // down. The interval only mounts while a marker shows.
  const now = useSecondTick(marker !== null);

  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [seenSyncedAt, setSeenSyncedAt] = useState<number | null>(null);
  const [anchorBox, setAnchorBox] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const openPopover = () => {
    const box = anchorRef.current?.getBoundingClientRect();
    setAnchorBox(box ? { top: box.bottom + 4, left: box.left } : null);
    restoreFocusRef.current = false;
    setOpen(true);
  };

  useEffect(() => {
    if (marker !== "synced") {
      setSeenSyncedAt(null);
      return;
    }
    setSeenSyncedAt(syncedAt ?? Date.now());
  }, [marker, syncedAt, change?.id]);

  useEffect(() => {
    if (!isFailedMarker(marker)) setOpen(false);
  }, [marker]);

  const kind: SyncMarkerKind | null = useMemo(() => {
    if (marker !== "synced") return marker;
    if (seenSyncedAt == null) return "synced";
    return now - seenSyncedAt < SYNCED_MARKER_MS ? "synced" : null;
  }, [marker, seenSyncedAt, now]);

  const close = () => {
    restoreFocusRef.current = true;
    setOpen(false);
  };

  // Focus moves into the popover when it opens, and back to the marker when
  // it closes.
  useEffect(() => {
    onPopoverOpenChange?.(open);
    if (!open) {
      if (restoreFocusRef.current) {
        restoreFocusRef.current = false;
        anchorRef.current?.querySelector<HTMLElement>("button")?.focus();
      }
      return;
    }
    const first = focusables(popoverRef.current)[0];
    (first ?? popoverRef.current)?.focus();
  }, [open, onPopoverOpenChange]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      event.preventDefault();
      restoreFocusRef.current = true;
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  if (!change || !kind) return null;

  const label = markerLabel(kind, change, now);
  const chip = (
    <span
      data-testid="sync-marker"
      data-state={kind}
      className={`inline-flex max-w-full items-center truncate rounded border px-1.5 py-0.5 text-xs font-medium leading-tight whitespace-nowrap ${MARKER_TONE[kind]}`}
    >
      {label}
    </span>
  );

  if (!isFailedMarker(kind)) return chip;

  // The portal places the panel on `document.body`. jsdom has no layout box,
  // so an unmeasured anchor falls back to the top-left corner.
  const popoverStyle: React.CSSProperties = anchorBox
    ? { top: anchorBox.top, left: anchorBox.left }
    : { top: 0, left: 0 };
  const reason = reasonFor(change.lastErrorCode);
  const rowNumber = change.rowNumber;
  const sheetsHref =
    spreadsheetId && rowNumber != null
      ? sheetsRowUrl(spreadsheetId, change.sheetId, rowNumber)
      : null;

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== "Tab") return;
    // The DataGrid moves focus with Tab. Keep it in the dialog.
    const nodes = focusables(popoverRef.current);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <span ref={anchorRef} className="relative inline-flex max-w-full">
      <Button
        variant="ghost"
        size="sm"
        className="!px-0 !py-0 hover:!bg-transparent"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}. Show details.`}
        onKeyDown={(event) => {
          // The DataGrid handles keys first. A click on the chip must not open
          // the row detail or the editor.
          if (event.key === "Escape" && open) {
            event.stopPropagation();
            close();
          }
        }}
        onClick={(event) => {
          event.stopPropagation();
          if (open) close();
          else openPopover();
        }}
        onDoubleClick={(event) => event.stopPropagation()}
      >
        {chip}
      </Button>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="presentation"
            data-testid="sync-popover-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) close();
            }}
            className="fixed inset-0 z-[1300]"
          >
            <div style={popoverStyle} className="absolute z-[1301]">
              <div
                ref={popoverRef}
                role="dialog"
                aria-label="Sync problem"
                tabIndex={-1}
                data-testid="sync-popover"
                className="flex w-64 flex-col gap-2 rounded-lg border border-od-border bg-od-surface p-3 text-left text-xs text-od-text shadow-lg"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={onKeyDown}
              >
                <span>{reason.message}</span>
                <span className="flex flex-wrap items-center gap-3">
                  {reason.action === "retry" && !viewOnly && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        close();
                        onRetry?.(change.id);
                      }}
                    >
                      Retry
                    </Button>
                  )}
                  {reason.action === "open" && sheetsHref && (
                    <a
                      href={sheetsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-od-primary-text underline underline-offset-2 hover:opacity-90"
                      onClick={(event) => event.stopPropagation()}
                    >
                      Open in Google Sheets
                    </a>
                  )}
                  {!viewOnly && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        close();
                        onDismiss?.(change.id);
                      }}
                    >
                      Dismiss
                    </Button>
                  )}
                </span>
              </div>
            </div>
          </div>,
          document.body
        )}
    </span>
  );
};
